import type { UserResponse } from '@malaberg/shared';
/**
 * UsersTab — T27.
 *
 * Admin+ view of all users.
 * Features:
 *   - Mantine Table with pagination and status/role filters
 *   - Invite user button → InviteUserModal
 *   - Per-row: Edit (modal), Reset password (confirm → banner), Impersonate (confirm → navigate)
 *   - Current user's Impersonate button is disabled (cannot impersonate self)
 */
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Group,
  Modal,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconEdit, IconKey, IconUser } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersApi } from '../../api/endpoints/users';
import { ApiError } from '../../api/errors';
import { EditUserModal } from '../../components/settings/EditUserModal';
import { InviteUserModal } from '../../components/settings/InviteUserModal';
import { TempPasswordBanner } from '../../components/settings/TempPasswordBanner';
import { useAuthStore } from '../../state/auth-store';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

// ─── Component ────────────────────────────────────────────────────────────────

export default function UsersTab() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);

  // ── List state ────────────────────────────────────────────────────────────
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // ── Modals / dialogs ──────────────────────────────────────────────────────
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserResponse | null>(null);

  // ── Impersonate flow ──────────────────────────────────────────────────────
  const [impersonateTarget, setImpersonateTarget] = useState<UserResponse | null>(null);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  // ── Reset password flow ───────────────────────────────────────────────────
  const [resetTarget, setResetTarget] = useState<UserResponse | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  // ── Auth store helpers for impersonation ──────────────────────────────────
  const accessToken = useAuthStore((s) => s.accessToken);
  const startImpersonating = useAuthStore((s) => s.startImpersonating);

  // ─── Data fetching ──────────────────────────────────────────────────────────

  async function fetchUsers() {
    setLoading(true);
    setListError(null);
    try {
      const result = await usersApi.list({
        status: (statusFilter as 'active' | 'inactive') || undefined,
        role: (roleFilter as 'writer' | 'admin' | 'system_owner') || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      const data = result as {
        ok: boolean;
        users?: UserResponse[];
        items?: UserResponse[];
        total: number;
      };
      setUsers(data.users ?? data.items ?? []);
      setTotal(data.total);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, roleFilter]);

  // ─── Impersonate ───────────────────────────────────────────────────────────

  async function handleImpersonateStart() {
    if (!impersonateTarget) return;
    setImpersonateLoading(true);
    try {
      const result = (await usersApi.impersonate(impersonateTarget.id)) as {
        ok: boolean;
        accessToken: string;
        accessTokenExpiresAt: string;
      };

      // startImpersonating handles the full token swap: saves the admin's
      // prev token, installs the new writer-scoped one, decodes the JWT for
      // imp_admin_id, and re-fetches /auth/me so `user` becomes the
      // impersonated user (the banner needs this to render).
      await startImpersonating(result.accessToken, new Date(result.accessTokenExpiresAt));

      setImpersonateTarget(null);
      navigate('/');
    } catch {
      // Close dialog — error surfaced via the auth store's fallback to logout
      setImpersonateTarget(null);
    } finally {
      setImpersonateLoading(false);
    }
  }

  // ─── Reset password ────────────────────────────────────────────────────────

  async function handleResetConfirm() {
    if (!resetTarget) return;
    setResetLoading(true);
    try {
      const result = (await usersApi.resetPassword(resetTarget.id)) as {
        ok: boolean;
        tempPassword: string;
      };
      setTempPassword(result.tempPassword);
      setResetTarget(null);
    } catch (err) {
      setResetTarget(null);
    } finally {
      setResetLoading(false);
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Stack data-testid="users-tab" gap="md">
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <Group justify="space-between">
        <Group gap="sm">
          <Select
            data-testid="filter-status"
            placeholder="All statuses"
            clearable
            data={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            value={statusFilter}
            onChange={(val) => {
              setStatusFilter(val);
              setPage(1);
            }}
            style={{ width: 160 }}
          />
          <Select
            data-testid="filter-role"
            placeholder="All roles"
            clearable
            data={[
              { value: 'writer', label: 'writer' },
              { value: 'admin', label: 'admin' },
              { value: 'system_owner', label: 'system_owner' },
            ]}
            value={roleFilter}
            onChange={(val) => {
              setRoleFilter(val);
              setPage(1);
            }}
            style={{ width: 160 }}
          />
        </Group>
        <Button data-testid="invite-user-btn" onClick={() => setInviteOpen(true)}>
          Invite user
        </Button>
      </Group>

      {/* ── Temp password banner (after reset) ───────────────────────────── */}
      {tempPassword && <TempPasswordBanner password={tempPassword} />}

      {/* ── Error state ───────────────────────────────────────────────────── */}
      {listError && (
        <Alert color="red" data-testid="users-list-error">
          {listError}
        </Alert>
      )}

      {/* ── Users table ───────────────────────────────────────────────────── */}
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Last login</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text ta="center" c="dimmed" size="sm">
                  Loading…
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && users.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text ta="center" c="dimmed" size="sm">
                  No users found
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading &&
            users.map((u) => {
              const isSelf = u.id === currentUser?.id;
              return (
                <Table.Tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <Table.Td>{u.name}</Table.Td>
                  <Table.Td>{u.email}</Table.Td>
                  <Table.Td>{u.role}</Table.Td>
                  <Table.Td>{u.status}</Table.Td>
                  <Table.Td>
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {/* Edit */}
                      <Tooltip label="Edit user">
                        <ActionIcon
                          data-testid="edit-user-btn"
                          variant="subtle"
                          size="sm"
                          onClick={() => setEditTarget(u)}
                          aria-label="Edit user"
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Tooltip>

                      {/* Reset password */}
                      <Tooltip label="Reset password">
                        <ActionIcon
                          data-testid="reset-password-btn"
                          variant="subtle"
                          size="sm"
                          color="orange"
                          onClick={() => setResetTarget(u)}
                          aria-label="Reset password"
                        >
                          <IconKey size={16} />
                        </ActionIcon>
                      </Tooltip>

                      {/* Impersonate */}
                      <Tooltip
                        label={isSelf ? 'You cannot impersonate yourself' : 'Impersonate this user'}
                      >
                        {/* Wrapper span needed so Tooltip works on a disabled button */}
                        <span>
                          <ActionIcon
                            data-testid="impersonate-btn"
                            variant="subtle"
                            size="sm"
                            color="blue"
                            disabled={isSelf}
                            data-disabled={isSelf || undefined}
                            onClick={() => !isSelf && setImpersonateTarget(u)}
                            aria-label="Impersonate user"
                          >
                            <IconUser size={16} />
                          </ActionIcon>
                        </span>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
        </Table.Tbody>
      </Table>

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      {total > PAGE_SIZE && (
        <Group justify="center">
          <Pagination
            total={Math.ceil(total / PAGE_SIZE)}
            value={page}
            onChange={setPage}
            data-testid="users-pagination"
          />
        </Group>
      )}

      {/* ── Impersonate confirm dialog ────────────────────────────────────── */}
      <Modal
        opened={impersonateTarget !== null}
        onClose={() => setImpersonateTarget(null)}
        title="Impersonate user"
        centered
        size="sm"
      >
        <Stack data-testid="impersonate-dialog">
          <Text>
            Start a read-only impersonation session as <strong>{impersonateTarget?.name}</strong>?
          </Text>
          <Group justify="flex-end">
            <Button
              data-testid="impersonate-cancel-btn"
              variant="default"
              onClick={() => setImpersonateTarget(null)}
            >
              Cancel
            </Button>
            <Button
              data-testid="impersonate-start-btn"
              color="blue"
              loading={impersonateLoading}
              onClick={handleImpersonateStart}
            >
              Start
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Reset password confirm dialog ─────────────────────────────────── */}
      <Modal
        opened={resetTarget !== null}
        onClose={() => setResetTarget(null)}
        title="Reset password"
        centered
        size="sm"
      >
        <Stack data-testid="reset-password-dialog">
          <Text>
            Reset password for <strong>{resetTarget?.name}</strong>? A new temporary password will
            be generated.
          </Text>
          <Group justify="flex-end">
            <Button
              data-testid="reset-cancel-btn"
              variant="default"
              onClick={() => setResetTarget(null)}
            >
              Cancel
            </Button>
            <Button
              data-testid="reset-confirm-btn"
              color="orange"
              loading={resetLoading}
              onClick={handleResetConfirm}
            >
              Reset password
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Invite modal ─────────────────────────────────────────────────── */}
      <InviteUserModal
        opened={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={fetchUsers}
      />

      {/* ── Edit user modal ───────────────────────────────────────────────── */}
      {editTarget && (
        <EditUserModal
          opened={editTarget !== null}
          onClose={() => setEditTarget(null)}
          onSuccess={fetchUsers}
          targetUser={editTarget}
        />
      )}
    </Stack>
  );
}
